import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'

/**
 * Emailul din care s-a nascut un task, deschis in CRM.
 *
 * A trait pana in august 2026 in `pages/TaskuriPage.tsx`, deci noul panou de task
 * (`PanouTask.tsx`) n-avea decat un link de cautare in Roundcube — care depinde de subiect si
 * rateaza exact cand ai mai multa nevoie de el. Mutat aici, il pot folosi amandoua ecranele.
 *
 * Continutul vine din `GET /tasks/:id/email`: intai din IMAP, iar daca emailul nu mai e acolo,
 * din cache-ul CRM-ului (atunci atasamentele lipsesc, si se spune pe fata).
 */

interface ParsedEmailAttachment {
  index: number
  filename: string
  contentType?: string
  size?: number
}

interface ParsedEmail {
  mailbox: string
  subject: string
  from: Array<{ name?: string; address?: string }>
  to: Array<{ name?: string; address?: string }>
  cc: Array<{ name?: string; address?: string }>
  date: string | null
  bodyText: string
  bodyHtml: string | null
  attachments: ParsedEmailAttachment[]
  source: 'imap' | 'db'
}

function formatBytes(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function EmailViewerModal({
  taskId,
  b4lEmailId,
  emailLink,
  onClose,
}: {
  taskId: number
  /** Message-ID-ul stocat pe task. Se arata la eroare: cel mai des acolo e problema. */
  b4lEmailId?: string | null
  /** Cautarea in webmail, ca ultima portita cand IMAP-ul si cache-ul dau gres. */
  emailLink?: string | null
  onClose: () => void
}) {
  const query = useQuery<ParsedEmail>({
    queryKey: ['email-content', taskId],
    queryFn: async () => (await api.get<ParsedEmail>(`/tasks/${taskId}/email`)).data,
    retry: 0,
    staleTime: 30_000,
  })

  const downloadAttachment = async (att: ParsedEmailAttachment) => {
    try {
      const res = await api.get(`/tasks/${taskId}/email-attachment?index=${att.index}`, {
        responseType: 'blob',
      })
      const blob = new Blob([res.data as BlobPart], {
        type: att.contentType ?? 'application/octet-stream',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = att.filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Atașamentul nu s-a putut descărca'
      toast.error(msg)
    }
  }

  const data = query.data

  const renderRecipients = (list: ParsedEmail['from']) =>
    list
      .map((p) => {
        if (p.name && p.address) return `${p.name} <${p.address}>`
        return p.address ?? p.name ?? ''
      })
      .filter(Boolean)
      .join(', ')

  return (
    <Modal
      open
      onClose={onClose}
      title="Vizualizare email"
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Închide
        </Button>
      }
    >
      {query.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă emailul...</p>}
      {query.isError && (
        <div className="space-y-2 text-sm">
          <p className="text-b4l-red-dark">
            {(query.error as { response?: { data?: { error?: { message?: string } } } })?.response
              ?.data?.error?.message ?? 'Emailul nu a putut fi încărcat din IMAP sau cache.'}
          </p>
          {/* La un refuz de acces (403), mesajul spune deja motivul; cauzele tehnice de mai jos ar
              induce in eroare. */}
          {(query.error as { response?: { status?: number } })?.response?.status !== 403 && (
            <>
              {b4lEmailId && (
                <div className="rounded border border-b4l-border bg-b4l-bg-soft p-2 font-mono text-[10px] text-b4l-text-muted">
                  <strong>Message-ID:</strong> {b4lEmailId}
                </div>
              )}
              <p className="text-xs text-b4l-text-muted">
                Posibile cauze: emailul a fost arhivat în alt folder cPanel, sau Message-ID-ul
                stocat pe task diferă de cel din inbox.
              </p>
              {emailLink && (
                <a
                  href={emailLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-b4l-blue-dark hover:underline"
                >
                  Deschide căutarea în webmail →
                </a>
              )}
            </>
          )}
        </div>
      )}
      {data && (
        <div className="space-y-3 text-sm">
          {/* Header */}
          <div className="space-y-1 rounded border border-b4l-border bg-b4l-bg-soft p-3">
            <div className="text-base font-semibold">{data.subject}</div>
            {data.from.length > 0 && (
              <div className="text-xs">
                <span className="text-b4l-text-dim">De la:</span>{' '}
                <span className="break-all">{renderRecipients(data.from)}</span>
              </div>
            )}
            {data.to.length > 0 && (
              <div className="text-xs">
                <span className="text-b4l-text-dim">Către:</span>{' '}
                <span className="break-all">{renderRecipients(data.to)}</span>
              </div>
            )}
            {data.cc.length > 0 && (
              <div className="text-xs">
                <span className="text-b4l-text-dim">CC:</span>{' '}
                <span className="break-all">{renderRecipients(data.cc)}</span>
              </div>
            )}
            {data.date && (
              <div className="text-xs">
                <span className="text-b4l-text-dim">Data:</span>{' '}
                {new Date(data.date).toLocaleString('en-GB')}
              </div>
            )}
            <div className="flex items-center gap-2 text-[10px] text-b4l-text-dim">
              <span>Folder: {data.mailbox}</span>
              {data.source === 'db' && (
                <span
                  className="rounded bg-b4l-amber-light px-1.5 py-0.5 text-b4l-amber-dark"
                  title="E-mailul nu mai e în căsuță — se afișează copia salvată în CRM. Atașamentele nu sunt disponibile."
                >
                  ⓘ din cache (atașamente indisponibile)
                </span>
              )}
            </div>
          </div>

          {/* Atasamente */}
          {data.attachments.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-b4l-text-dim">
                Atașamente ({data.attachments.length})
              </div>
              <div className="space-y-1">
                {data.attachments.map((att) => (
                  <button
                    key={att.index}
                    onClick={() => void downloadAttachment(att)}
                    className="flex w-full items-center gap-2 rounded border border-b4l-border bg-white px-2 py-1.5 text-left text-xs hover:bg-b4l-bg-soft"
                  >
                    <svg
                      className="h-4 w-4 text-b4l-text-dim"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                    <span className="flex-1 break-all">{att.filename}</span>
                    {att.size != null && (
                      <span className="text-b4l-text-dim">{formatBytes(att.size)}</span>
                    )}
                    <span className="text-b4l-blue-dark">↓</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Body */}
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-b4l-text-dim">
              Conținut
            </div>
            {data.bodyText ? (
              <pre className="whitespace-pre-wrap break-words rounded border border-b4l-border bg-white p-3 font-sans text-sm">
                {data.bodyText}
              </pre>
            ) : data.bodyHtml ? (
              <div className="rounded border border-b4l-border bg-white p-3 text-xs italic text-b4l-text-muted">
                Email cu format HTML — folosește butonul de mai jos pentru a-l vedea în webmail.
              </div>
            ) : (
              <p className="text-xs text-b4l-text-muted">(corp gol)</p>
            )}
          </div>

          {emailLink && (
            <div className="text-xs">
              <a
                href={emailLink}
                target="_blank"
                rel="noreferrer"
                className="text-b4l-blue-dark hover:underline"
              >
                Deschide în webmail (Roundcube) →
              </a>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
